import { Button, Divider, Flex, LoadingOverlay, Paper, Text } from "@mantine/core";
import IconDeviceFloppy from "@tabler/icons-react/dist/esm/icons/IconDeviceFloppy.mjs";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import MailSenderRadio from "./components/MailSenderRadio";
import RestSection from "./components/RestSection";
import SmtpSection from "./components/SmtpSection";
import {
  useCurrentUserQuery,
  useRestConfigQuery,
  useSaveEmailConfigMutation,
  useSmtpQuery,
} from "./useEmailSettings.logic";

const defaultValues = {
  smtp: { mailFrom: "", server: "", port: "", username: "", password: "" },
  rest: { from: "" },
  mailSender: "",
};

const EmailSettings = () => {
  const { t } = useTranslation();
  const { data: smtpData, isLoading: smtpLoading } = useSmtpQuery();
  const { data: restConfigData, isLoading: restLoading } = useRestConfigQuery();
  const { data: userData, isLoading: userLoading } = useCurrentUserQuery();
  const saveMutation = useSaveEmailConfigMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields, isDirty },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (!smtpData && !restConfigData && !userData) return;
    reset({
      smtp: {
        mailFrom: smtpData?.mailFrom ?? "",
        server: smtpData?.server ?? "",
        port: smtpData?.port ?? "",
        username: smtpData?.username ?? "",
        password: smtpData?.password ?? "",
      },
      rest: { from: restConfigData?.from ?? "" },
      mailSender: userData?.tenant?.mailSender ?? "",
    });
  }, [smtpData, restConfigData, userData, reset]);

  const onSubmit = async (values) => {
    const payload = {};
    if (dirtyFields.smtp) payload.smtp = values.smtp;
    if (dirtyFields.rest) payload.rest = values.rest;
    if (dirtyFields.mailSender) payload.mailSender = values.mailSender;
    if (!Object.keys(payload).length) return;

    saveMutation.mutate(payload, {
      onSuccess: () => reset(values),
    });
  };

  const loading = smtpLoading || restLoading || userLoading;

  return (
    <Paper pos="relative" withBorder radius="md" style={{ overflow: "hidden" }}>
      <LoadingOverlay visible={loading || saveMutation.isPending} overlayBlur={2} />
      <Flex justify="space-between" align="center" p="sm">
        <Text size="md">
          {t("settings.emails")}
        </Text>
        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          disabled={!isDirty}
          loading={saveMutation.isPending}
        >
          SAVE
        </Button>
      </Flex>
      <Divider color="secondary.1" />
      <Paper bg="secondary.0" p="md" radius={0}>
        <Flex direction="column" gap="md">
          <MailSenderRadio control={control} />
          <Divider color="secondary.1" />
          <SmtpSection control={control} />
          <Divider color="secondary.1" />
          <RestSection control={control} />
        </Flex>
      </Paper>
    </Paper>
  );
};

export default EmailSettings;
